import { useQuery } from 'urql';
import { Q_ME, Q_BILLING } from '@/lib/queries';
import { useAuth } from '@/auth/AuthContext';
import { isFirebaseEnabled } from '@/auth/firebase';

const SITE_URL = (import.meta.env.VITE_SITE_URL ?? (import.meta.env.DEV ? 'http://localhost:5181' : '')).replace(/\/$/, '');

export default function ProfileRoute() {
  const { user, signOut } = useAuth();
  const [me] = useQuery({ query: Q_ME });
  const [billing] = useQuery({ query: Q_BILLING });

  const u = me.data?.me?.user;
  const workspaces = me.data?.me?.workspaces ?? [];
  const credits = me.data?.me?.creditsRemaining ?? 0;
  const plan = billing.data?.billingUsage?.plan;

  const handleLogout = async () => {
    await signOut();                       // Firebase sign-out (no-op in mock mode)
    localStorage.removeItem('access_token');
    localStorage.removeItem('workspace_id');
    location.href = SITE_URL;              // hand back to the marketing site
  };

  return (
    <div className="p-8 max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold text-stone-900">Profile</h1>

      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xl font-semibold">
            {(u?.name ?? user?.displayName ?? u?.email ?? '?').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <p className="text-lg font-semibold text-stone-900">{u?.name ?? user?.displayName ?? '—'}</p>
            <p className="text-sm text-stone-500">{u?.email ?? user?.email ?? '—'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-3">
        <h2 className="font-semibold text-stone-900">Account</h2>
        <Row label="Plan" value={plan ?? '—'} />
        <Row label="AI credits remaining" value={credits.toLocaleString()} />
        <Row label="Auth provider" value={isFirebaseEnabled ? 'Firebase' : 'Mock (dev)'} />
        <Row label="Workspaces" value={String(workspaces.length)} />
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <h2 className="font-semibold text-stone-900 mb-3">Workspaces</h2>
        <div className="space-y-2">
          {workspaces.map((w: any) => (
            <div key={w.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
              <div>
                <p className="text-sm font-medium text-stone-900">{w.name}</p>
                <p className="text-[11px] text-stone-500">{w.slug} · {w.plan}</p>
              </div>
              <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded bg-violet-50 text-violet-700">{w.role}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-xs text-stone-400">Signed in as {u?.email ?? user?.email ?? 'demo'}</p>
        <button onClick={handleLogout} className="px-5 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">
          Log out
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-stone-500">{label}</span>
      <span className="font-medium text-stone-800">{value}</span>
    </div>
  );
}
